import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView, Image, ActivityIndicator, Platform, useWindowDimensions, Switch } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { auth } from '../firebaseConfig';
import { getUserProfile, updateUserProfile, uploadProfilePicture, changeUserPassword, getUserScopeFilter, updateUserScopeFilter } from '../services/profileService';
import { getUserPreferences, updateUserPreferences, seedHardcodedExamples, getProjectExamples } from '../services/firestoreService';
import { HARDCODED_EXAMPLES } from '../hardcodedExamples';
import UserMenu from '../components/UserMenu';

export default function ProfileScreen({ navigation }) {
  const { themeMode, colors, glass, isDark, gradients, setTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Header scroll state
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Form fields
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [calendlyUrl, setCalendlyUrl] = useState('');
  
  // Password change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // App preferences
  const [strictAdherenceDefault, setStrictAdherenceDefault] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('all'); // all, organization, personal
  
  // Data management
  const [examplesCount, setExamplesCount] = useState(0);
  const [seedingExamples, setSeedingExamples] = useState(false);

  useEffect(() => {
    loadProfile();
    loadExamplesCount();
    document.title = 'Flomodo - Profile';
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle scroll for header behavior
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          
          // Show/hide based on scroll direction
          if (scrollY > lastScrollY && scrollY > 100) {
            setIsVisible(false);
          } else {
            setIsVisible(true);
          }
          
          setIsScrolled(scrollY > 50);
          setLastScrollY(scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const loadProfile = async () => {
    try {
      const [userProfile, prefs, userScopeFilter] = await Promise.all([
        getUserProfile(auth.currentUser.uid),
        getUserPreferences(auth.currentUser.uid),
        getUserScopeFilter(auth.currentUser.uid)
      ]);
      
      setProfile(userProfile);
      setDisplayName(userProfile.displayName || '');
      setBio(userProfile.bio || '');
      setWebsiteUrl(userProfile.websiteUrl || '');
      setGithubUrl(userProfile.githubUrl || '');
      setCalendlyUrl(userProfile.calendlyUrl || '');
      setStrictAdherenceDefault(prefs?.strictAdherenceDefault || false);
      setScopeFilter(userScopeFilter);
    } catch (error) {
      console.error('Error loading profile:', error);
      alert('Error loading profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadExamplesCount = async () => {
    try {
      const examples = await getProjectExamples();
      setExamplesCount(examples.length);
    } catch (error) {
      console.error('Error loading examples count:', error);
    }
  };

  const handleSeedExamples = async (forceReseed = false) => {
    const message = forceReseed 
      ? 'This will DELETE all existing examples and re-import 18 examples with updated data (technologies & case studies). Continue?' 
      : 'This will add 18 pre-loaded project examples to your account. Continue?';
    
    if (confirm(message)) {
      setSeedingExamples(true);
      try {
        await seedHardcodedExamples(HARDCODED_EXAMPLES, forceReseed);
        await loadExamplesCount();
        const action = forceReseed ? 'Reseeded' : 'Successfully added';
        alert(`${action} 18 project examples with technologies and case studies!`);
      } catch (error) {
        alert('Error seeding examples: ' + error.message);
      } finally {
        setSeedingExamples(false);
      }
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateUserProfile(auth.currentUser.uid, {
        displayName,
        bio,
        websiteUrl,
        githubUrl,
        calendlyUrl,
      });
      
      alert('✅ Profile updated successfully!');
      await loadProfile(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImagePick = async () => {
    if (Platform.OS === 'web') {
      // Web file picker
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          setUploadingImage(true);
          try {
            // Create object URL for preview
            const imageUrl = URL.createObjectURL(file);
            
            // Upload to Firebase Storage
            await uploadProfilePicture(auth.currentUser.uid, imageUrl);
            
            alert('✅ Profile picture updated!');
            await loadProfile();
          } catch (error) {
            console.error('Error uploading image:', error);
            alert('Error uploading image: ' + error.message);
          } finally {
            setUploadingImage(false);
          }
        }
      };
      input.click();
    } else {
      // Mobile would use react-native-image-picker or expo-image-picker
      alert('Image picker not implemented for mobile yet');
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    
    if (!newPassword || !confirmPassword) {
      setPasswordError('Please fill in both password fields');
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await changeUserPassword(newPassword);
      alert('✅ Password changed successfully!');
      setShowPasswordChange(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/requires-recent-login') {
        setPasswordError('Please sign out and sign back in before changing your password');
      } else {
        setPasswordError(error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStrictAdherence = async (value) => {
    setStrictAdherenceDefault(value);
    try {
      await updateUserPreferences(auth.currentUser.uid, {
        strictAdherenceDefault: value,
      });
    } catch (error) {
      console.error('Error updating strict adherence preference:', error);
      alert('Failed to save preference');
      // Revert on error
      setStrictAdherenceDefault(!value);
    }
  };

  const handleScopeFilterChange = async (newFilter) => {
    setScopeFilter(newFilter);
    try {
      await updateUserScopeFilter(auth.currentUser.uid, newFilter);
    } catch (error) {
      console.error('Error updating scope filter:', error);
      alert('Failed to save scope filter preference');
    }
  };

  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundImage: gradients.background }]}>
        <ActivityIndicator size="large" color={colors.accent} />
      </View>
    );
  }

  const profileImageUrl = profile?.photoURL || auth.currentUser?.photoURL;

  return (
    <View style={[styles.container, { backgroundImage: gradients.background }]}>
      {/* Custom Header with landing page styling */}
      <View style={[
        styles.headerContainer,
        isMobile && styles.headerContainerMobile,
        isScrolled && styles.headerScrolled,
        isScrolled && !isMobile && { 
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.7)' : 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: isDark 
            ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
            : '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
        isMobile && {
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        },
        !isScrolled && !isMobile && { backgroundColor: 'transparent' },
        !isVisible && !isMobile && styles.headerHidden,
      ]}>
        <View style={[styles.header, isMobile && styles.headerMobile]}>
          <TouchableOpacity 
            style={[styles.logoButton, isMobile && styles.logoButtonMobile]}
            onPress={() => navigation.navigate('dashboard')}
          >
            <Image 
              source={require('../assets/logo.png')} 
              style={[styles.logo, isMobile && styles.logoMobile]}
              resizeMode="contain"
            />
          </TouchableOpacity>
          
          <View style={styles.headerRight}>
            <UserMenu navigation={navigation} currentScreen="profile" />
          </View>
        </View>
      </View>

      <div style={{ flex: 1, overflow: 'auto' }}>
        <View style={styles.contentContainer}>
        {/* Profile Picture Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Profile Picture</Text>
          <View style={styles.profilePictureContainer}>
            <View style={styles.profilePictureWrapper}>
              {profileImageUrl ? (
                <Image source={{ uri: profileImageUrl }} style={styles.profilePicture} />
              ) : (
                <View style={[styles.profilePicturePlaceholder, { backgroundColor: colors.accent }]}>
                  <Text style={[styles.profilePicturePlaceholderText, { color: '#fff' }]}>
                    {displayName.charAt(0).toUpperCase() || 'U'}
                  </Text>
                </View>
              )}
            </View>
            <TouchableOpacity 
              style={[styles.changePhotoButton, { backgroundColor: colors.accent }]}
              onPress={handleImagePick}
              disabled={uploadingImage}
            >
              {uploadingImage ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={[styles.changePhotoButtonText, { color: '#fff' }]}>Change Photo</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {/* Basic Info Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Basic Information</Text>
          
          <Text style={[styles.label, { color: colors.text }]}>Display Name</Text>
          <TextInput
            style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            value={displayName}
            onChangeText={setDisplayName}
            placeholder="Your name"
            placeholderTextColor={colors.textTertiary}
          />

          <Text style={[styles.label, { color: colors.text }]}>Email</Text>
          <TextInput
            style={[styles.input, styles.inputDisabled, { ...glass, backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }]}
            value={auth.currentUser?.email}
            editable={false}
          />
          <Text style={[styles.helperText, { color: colors.textSecondary }]}>Email cannot be changed</Text>

          <Text style={[styles.label, { color: colors.text }]}>Bio</Text>
          <TextInput
            style={[styles.input, styles.textArea, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            value={bio}
            onChangeText={setBio}
            placeholder="Tell us about yourself..."
            placeholderTextColor={colors.textTertiary}
            multiline
            numberOfLines={4}
          />
        </View>

        {/* Links Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Professional Links</Text>
          <Text style={[styles.sectionDesc, { color: colors.textSecondary }]}>
            These links will be automatically used in Q&A answers when relevant
          </Text>
          
          <Text style={[styles.label, { color: colors.text }]}>Website URL</Text>
          <TextInput
            style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            value={websiteUrl}
            onChangeText={setWebsiteUrl}
            placeholder="https://yourwebsite.com"
            placeholderTextColor={colors.textTertiary}
            autoCapitalize="none"
          />

          <Text style={[styles.label, { color: colors.text }]}>GitHub URL</Text>
          <TextInput
            style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            value={githubUrl}
            onChangeText={setGithubUrl}
            placeholder="https://github.com/yourusername"
            placeholderTextColor={colors.textTertiary}
            autoCapitalize="none"
          />

          <Text style={[styles.label, { color: colors.text }]}>Calendly URL</Text>
          <Text style={[styles.helpText, { color: colors.textSecondary }]}>
            Add your scheduling link to be included in Contra proposals
          </Text>
          <TextInput
            style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
            value={calendlyUrl}
            onChangeText={setCalendlyUrl}
            placeholder="https://calendly.com/yourusername"
            placeholderTextColor={colors.textTertiary}
            autoCapitalize="none"
          />
        </View>

        {/* Save Button */}
        <TouchableOpacity 
          style={[styles.saveButton, { backgroundColor: colors.accent }]}
          onPress={handleSaveProfile}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={[styles.saveButtonText, { color: '#fff' }]}>Save Changes</Text>
          )}
        </TouchableOpacity>

        {/* Password Change Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Security</Text>
          
          {!showPasswordChange ? (
            <TouchableOpacity 
              style={[styles.secondaryButton, { backgroundColor: colors.secondary, borderColor: colors.border }]}
              onPress={() => setShowPasswordChange(true)}
            >
              <Text style={[styles.secondaryButtonText, { color: colors.text }]}>Change Password</Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.passwordChangeContainer}>
              <Text style={[styles.label, { color: colors.text }]}>New Password</Text>
              <TextInput
                style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                value={newPassword}
                onChangeText={setNewPassword}
                placeholder="Enter new password"
                placeholderTextColor={colors.textTertiary}
                secureTextEntry
              />

              <Text style={[styles.label, { color: colors.text }]}>Confirm Password</Text>
              <TextInput
                style={[styles.input, { ...glass, backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder="Confirm new password"
                placeholderTextColor={colors.textTertiary}
                secureTextEntry
              />

              {passwordError ? (
                <Text style={styles.errorText}>{passwordError}</Text>
              ) : null}

              <View style={styles.passwordButtonRow}>
                <TouchableOpacity 
                  style={[styles.secondaryButton, { flex: 1, marginRight: 10, backgroundColor: colors.secondary, borderColor: colors.border }]}
                  onPress={() => {
                    setShowPasswordChange(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                >
                  <Text style={[styles.secondaryButtonText, { color: colors.text }]}>Cancel</Text>
                </TouchableOpacity>
                
                <TouchableOpacity 
                  style={[styles.saveButton, { flex: 1, backgroundColor: colors.accent }]}
                  onPress={handleChangePassword}
                  disabled={saving}
                >
                  {saving ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={[styles.saveButtonText, { color: '#fff' }]}>Update Password</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>

        {/* App Settings */}
        <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1, borderRadius: 12, padding: 20 }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>App Settings</Text>
          
          {/* Theme Selection */}
          <Text style={[styles.label, { color: colors.text, marginBottom: 10 }]}>Theme</Text>
          <View style={{ gap: 10, marginBottom: 20 }}>
            <TouchableOpacity
              style={[
                styles.themeOption,
                {
                  backgroundColor: themeMode === 'light' ? colors.accent + '20' : colors.secondary,
                  borderColor: themeMode === 'light' ? colors.accent : colors.border,
                  borderWidth: 2,
                  borderRadius: 8,
                  padding: 12,
                },
              ]}
              onPress={() => setTheme('light')}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View>
                  <Text style={[styles.themeLabel, { color: colors.text, fontWeight: '600' }]}>☀️ Light Mode</Text>
                  <Text style={[styles.themeDescription, { color: colors.textSecondary, fontSize: 13 }]}>Bright and clean interface</Text>
                </View>
                {themeMode === 'light' && (
                  <View style={[styles.selectedBadge, { backgroundColor: colors.accent, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 }]}>
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>✓</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.themeOption,
                {
                  backgroundColor: themeMode === 'dark' ? colors.accent + '20' : colors.secondary,
                  borderColor: themeMode === 'dark' ? colors.accent : colors.border,
                  borderWidth: 2,
                  borderRadius: 8,
                  padding: 12,
                },
              ]}
              onPress={() => setTheme('dark')}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View>
                  <Text style={[styles.themeLabel, { color: colors.text, fontWeight: '600' }]}>🌙 Dark Mode</Text>
                  <Text style={[styles.themeDescription, { color: colors.textSecondary, fontSize: 13 }]}>Easy on the eyes</Text>
                </View>
                {themeMode === 'dark' && (
                  <View style={[styles.selectedBadge, { backgroundColor: colors.accent, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 }]}>
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>✓</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.themeOption,
                {
                  backgroundColor: themeMode === 'system' ? colors.accent + '20' : colors.secondary,
                  borderColor: themeMode === 'system' ? colors.accent : colors.border,
                  borderWidth: 2,
                  borderRadius: 8,
                  padding: 12,
                },
              ]}
              onPress={() => setTheme('system')}
            >
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View>
                  <Text style={[styles.themeLabel, { color: colors.text, fontWeight: '600' }]}>⚙️ System</Text>
                  <Text style={[styles.themeDescription, { color: colors.textSecondary, fontSize: 13 }]}>Matches your device settings</Text>
                </View>
                {themeMode === 'system' && (
                  <View style={[styles.selectedBadge, { backgroundColor: colors.accent, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 4 }]}>
                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>✓</Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          </View>

          {/* Strict Adherence Preference */}
          <View style={[styles.preferenceRow, { backgroundColor: colors.secondary, borderRadius: 8, padding: 15, marginTop: 10 }]}>
            <View style={{ flex: 1, marginRight: 15 }}>
              <Text style={[styles.preferenceTitle, { color: colors.text, fontWeight: '600', fontSize: 15 }]}>
                Strict Adherence by Default
              </Text>
              <Text style={[styles.preferenceDescription, { color: colors.textSecondary, fontSize: 13, marginTop: 4 }]}>
                When enabled, all proposal generators will have strict adherence turned on by default
              </Text>
            </View>
            <Switch
              value={strictAdherenceDefault}
              onValueChange={handleToggleStrictAdherence}
              trackColor={{ false: colors.border, true: colors.accent }}
              thumbColor="#fff"
            />
          </View>

          {/* Scope Filter Preference */}
          <View style={[styles.preferenceRow, { backgroundColor: colors.secondary, borderRadius: 8, padding: 15, marginTop: 10 }]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.preferenceTitle, { color: colors.text, fontWeight: '600', fontSize: 15, marginBottom: 8 }]}>
                Templates & Examples Filter
              </Text>
              <Text style={[styles.preferenceDescription, { color: colors.textSecondary, fontSize: 13, marginBottom: 12 }]}>
                Choose which templates and examples to display in the Proposal Generator and Settings
              </Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity
                  onPress={() => handleScopeFilterChange('all')}
                  style={[
                    styles.filterButton,
                    {
                      backgroundColor: scopeFilter === 'all' ? colors.accent : colors.background,
                      borderColor: scopeFilter === 'all' ? colors.accent : colors.border,
                      borderWidth: 1,
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderRadius: 6,
                      flex: 1,
                      alignItems: 'center'
                    }
                  ]}
                >
                  <Text style={{ color: scopeFilter === 'all' ? '#fff' : colors.text, fontSize: 13, fontWeight: '600' }}>
                    All
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => handleScopeFilterChange('organization')}
                  style={[
                    styles.filterButton,
                    {
                      backgroundColor: scopeFilter === 'organization' ? colors.accent : colors.background,
                      borderColor: scopeFilter === 'organization' ? colors.accent : colors.border,
                      borderWidth: 1,
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderRadius: 6,
                      flex: 1,
                      alignItems: 'center'
                    }
                  ]}
                >
                  <Text style={{ color: scopeFilter === 'organization' ? '#fff' : colors.text, fontSize: 13, fontWeight: '600' }}>
                    Organization
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => handleScopeFilterChange('personal')}
                  style={[
                    styles.filterButton,
                    {
                      backgroundColor: scopeFilter === 'personal' ? colors.accent : colors.background,
                      borderColor: scopeFilter === 'personal' ? colors.accent : colors.border,
                      borderWidth: 1,
                      paddingVertical: 8,
                      paddingHorizontal: 12,
                      borderRadius: 6,
                      flex: 1,
                      alignItems: 'center'
                    }
                  ]}
                >
                  <Text style={{ color: scopeFilter === 'personal' ? '#fff' : colors.text, fontSize: 13, fontWeight: '600' }}>
                    Personal
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>

        {/* Data Management */}
        <View style={[styles.section, { ...glass, backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1, borderRadius: 12, padding: 20 }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Data Management</Text>
          <Text style={[styles.sectionDesc, { color: colors.textSecondary }]}>
            Import pre-loaded project examples to showcase your work in proposals
          </Text>
          
          {/* Examples Count */}
          <View style={[styles.dataInfoRow, { backgroundColor: colors.secondary, borderRadius: 8, padding: 15, marginBottom: 15 }]}>
            <Text style={[styles.dataInfoLabel, { color: colors.textSecondary }]}>Current Project Examples:</Text>
            <Text style={[styles.dataInfoValue, { color: colors.text, fontWeight: '600', fontSize: 18 }]}>
              {examplesCount}
            </Text>
          </View>

          {/* Import Buttons */}
          <View style={{ gap: 10 }}>
            {examplesCount === 0 ? (
              <TouchableOpacity 
                onPress={() => handleSeedExamples(false)}
                style={[styles.importButton, { backgroundColor: '#6a5aff' }]}
                disabled={seedingExamples}
              >
                {seedingExamples ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Text style={styles.importButtonIcon}>🌱</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.importButtonText, { color: '#fff' }]}>Import 18 Example Projects</Text>
                      <Text style={[styles.importButtonDesc, { color: '#fff', opacity: 0.9 }]}>
                        Add pre-loaded portfolio examples
                      </Text>
                    </View>
                  </>
                )}
              </TouchableOpacity>
            ) : (
              <TouchableOpacity 
                onPress={() => handleSeedExamples(true)}
                style={[styles.importButton, { backgroundColor: '#ff6a5a' }]}
                disabled={seedingExamples}
              >
                {seedingExamples ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <>
                    <Text style={styles.importButtonIcon}>🔄</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.importButtonText, { color: '#fff' }]}>Reseed All Examples</Text>
                      <Text style={[styles.importButtonDesc, { color: '#fff', opacity: 0.9 }]}>
                        Replace all existing examples with fresh data
                      </Text>
                    </View>
                  </>
                )}
              </TouchableOpacity>
            )}

            <TouchableOpacity 
              onPress={() => navigation.navigate('settings')}
              style={[styles.manageButton, { backgroundColor: colors.secondary, borderColor: colors.border }]}
            >
              <Text style={styles.manageButtonIcon}>⚙️</Text>
              <View style={{ flex: 1 }}>
                <Text style={[styles.manageButtonText, { color: colors.text }]}>Manage Templates & Examples</Text>
                <Text style={[styles.manageButtonDesc, { color: colors.textSecondary }]}>
                  Create, edit, and organize your content
                </Text>
              </View>
              <Text style={[styles.manageButtonArrow, { color: colors.textTertiary }]}>›</Text>
            </TouchableOpacity>
          </View>

          <View style={[styles.infoBox, { backgroundColor: colors.accent + '10', borderLeftColor: colors.accent, borderLeftWidth: 3, borderRadius: 8, padding: 12, marginTop: 15 }]}>
            <Text style={[styles.infoBoxText, { color: colors.textSecondary, fontSize: 13 }]}>
              💡 These examples will be available when generating proposals and can be customized in Templates & Examples settings
            </Text>
          </View>
        </View>

        {/* Account Info */}
        <View style={[styles.section, styles.accountInfoSection]}>
          <Text style={[styles.accountInfoText, { color: colors.textSecondary }]}>
            Account created: {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Unknown'}
          </Text>
          <Text style={[styles.accountInfoText, { color: colors.textSecondary }]}>
            User ID: {auth.currentUser?.uid}
          </Text>
        </View>
        </View>
      </div>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Header Styles (matching landing page)
  headerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 2000,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  headerScrolled: {
    top: 10,
    left: 10,
    right: 10,
    borderRadius: 20,
    maxWidth: 'calc(100% - 20px)',
  },
  headerHidden: {
    transform: 'translateY(-120px)',
    opacity: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    maxWidth: 1400,
    marginHorizontal: 'auto',
    width: '100%',
  },
  logoButton: {
    padding: 4,
  },
  logo: {
    width: 240,
    height: 68,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    zIndex: 2001,
  },
  
  // Mobile & Tablet Styles (< 1024px)
  headerContainerMobile: {
    borderRadius: 0,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  headerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  logoButtonMobile: {
    padding: 2,
  },
  logoMobile: {
    width: 140,
    height: 40,
  },
  
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
    paddingTop: 120,
  },
  section: {
    marginBottom: 30,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 10,
  },
  sectionDesc: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 15,
  },
  profilePictureContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
  },
  profilePictureWrapper: {
    marginRight: 20,
  },
  profilePicture: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#f1f5f9',
  },
  profilePicturePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#4a9eff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profilePicturePlaceholderText: {
    fontSize: 40,
    fontWeight: 'bold',
    color: '#fff',
  },
  changePhotoButton: {
    backgroundColor: '#4a9eff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  changePhotoButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 8,
    marginTop: 15,
  },
  helpText: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 8,
    fontStyle: 'italic',
  },
  input: {
    borderRadius: 12,
    padding: 12,
    fontSize: 16,
    borderWidth: 1,
  },
  inputDisabled: {
    opacity: 0.5,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  helperText: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 5,
  },
  saveButton: {
    backgroundColor: '#4a9eff',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginVertical: 10,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    backgroundColor: '#f8fafc',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  secondaryButtonText: {
    color: '#1e293b',
    fontSize: 16,
    fontWeight: '600',
  },
  passwordChangeContainer: {
    marginTop: 10,
  },
  passwordButtonRow: {
    flexDirection: 'row',
    marginTop: 15,
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    marginTop: 10,
  },
  accountInfoSection: {
    backgroundColor: '#f8fafc',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  accountInfoText: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 5,
  },
  dataInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dataInfoLabel: {
    fontSize: 14,
    color: '#64748b',
  },
  dataInfoValue: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1e293b',
  },
  importButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 12,
  },
  importButtonIcon: {
    fontSize: 24,
  },
  importButtonText: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  importButtonDesc: {
    fontSize: 13,
  },
  manageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  manageButtonIcon: {
    fontSize: 24,
  },
  manageButtonText: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  manageButtonDesc: {
    fontSize: 13,
  },
  manageButtonArrow: {
    fontSize: 28,
    fontWeight: '300',
  },
  infoBox: {
    borderRadius: 8,
    padding: 12,
  },
  infoBoxText: {
    fontSize: 13,
    lineHeight: 18,
  },
});

